import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { poursAPI, type Pour, type PourDetail, type PourLocation2 } from '../../services/pours';
import productsService from '../../services/products';
import { apiErrorText } from '../../utils/apiError';
import { useCompanyStrengthUnit } from '../../utils/useCompanyStrengthUnit';
import { valueWithUnit } from '../../utils/maturityDisplay';

const VIRTUAL_LOGGER_ID = 'VIRTUAL-LOGGER-001';

type CurveRole = 'auto' | 'core' | 'surface' | 'ambient';

const ROLE_OPTIONS: { value: CurveRole; label: string; help: string }[] = [
  { value: 'auto', label: 'Auto', help: 'Random ±3°C offset' },
  { value: 'core', label: 'Core', help: 'Full peak, slow cooling (hottest)' },
  { value: 'surface', label: 'Surface', help: 'Reduced peak, fast cooling' },
  { value: 'ambient', label: 'Ambient', help: 'Tracks air temp, no hydration curve' },
];

// Quick-created pours name their locations Core/Surface/Ambient/Mid, so
// matching by name pre-assigns the right curve role automatically.
const roleFromName = (name?: string): CurveRole => {
  const n = (name || '').toLowerCase();
  if (n.includes('core')) return 'core';
  if (n.includes('surface')) return 'surface';
  if (n.includes('ambient') || n.includes('air')) return 'ambient';
  return 'auto';
};

// Always plain text: a 422 sends `detail` as an array of objects.
const extractApiError = (err: any): string => apiErrorText(err, 'Unknown error');

// Curing hours from XHours: days for StopType '31', otherwise hours. A '31'
// pour stops XHours days after its start and a run moves the start to the
// data start, so leave a spare day (2 h on a one-day pour): the pour is then
// still running when the data, live readings included, ends.
const defaultCuringHours = (detail: PourDetail, fallback: number): number => {
  if (!detail.XHours || detail.XHours <= 0) return fallback;
  if (detail.StopType !== '31') return detail.XHours;
  const stopHours = detail.XHours * 24;
  return stopHours > 24 ? stopHours - 24 : Math.max(1, stopHours - 2);
};

/** The pours the simulator can drive: In Progress and Pending only, filtered
 *  by the server rather than downloading every pour. */
const loadLivePours = async (): Promise<Pour[]> => {
  const [inProgress, pending] = await Promise.all([
    poursAPI.getAllPourPages({ status: 'In progress' }),
    poursAPI.getAllPourPages({ status: 'Pending' }),
  ]);
  return [...inProgress, ...pending];
};

const SimulatorPage: React.FC = () => {
  const { t } = useTranslation();

  const [pours, setPours] = useState<Pour[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string>('');

  // Quick-create test pour
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [qcProjectId, setQcProjectId] = useState('');
  const [qcKind, setQcKind] = useState<'temp_only' | 'maturity'>('temp_only');
  const [qcLocations, setQcLocations] = useState('3');
  const [qcHours, setQcHours] = useState('48');
  const [qcName, setQcName] = useState('');
  const [qcRunning, setQcRunning] = useState(false);
  const [qcResult, setQcResult] = useState<string>('');

  const [pourId, setPourId] = useState('');
  const [mode, setMode] = useState<'bulk' | 'realtime'>('bulk');
  const [hours, setHours] = useState('24');
  const [baseTemp, setBaseTemp] = useState('20');
  const [peakTemp, setPeakTemp] = useState('65');
  const [peakPercent, setPeakPercent] = useState('30');
  const [noise, setNoise] = useState('0.5');
  const [realtimeInterval, setRealtimeInterval] = useState('10');
  const [realtimeDuration, setRealtimeDuration] = useState('10');
  const [includeWeather, setIncludeWeather] = useState(false);
  const [weatherTemp, setWeatherTemp] = useState('25');
  const [weatherHumidity, setWeatherHumidity] = useState('60');
  const [weatherWind, setWeatherWind] = useState('11');
  const [startTime, setStartTime] = useState('');

  const [selectedPourDetail, setSelectedPourDetail] = useState<PourDetail | null>(null);
  // The grade is in its mix design company's strength unit, as on the mix
  // design and pour setup pages (the pour's company when the mix design
  // cannot be read); no unit until known.
  const [gradeCompanyId, setGradeCompanyId] = useState<string | null>(null);
  useEffect(() => {
    setGradeCompanyId(null);
    const detail = selectedPourDetail;
    if (!detail || detail.ProductStrengthGrade == null) return undefined;
    const pourCompanyId = (detail.CompanyID as string | undefined) || null;
    if (!detail.ProductID) {
      setGradeCompanyId(pourCompanyId);
      return undefined;
    }
    let cancelled = false;
    productsService
      .getProduct(detail.ProductID)
      .then((product) => {
        if (!cancelled) setGradeCompanyId(product?.company_id || pourCompanyId);
      })
      .catch(() => {
        if (!cancelled) setGradeCompanyId(pourCompanyId);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPourDetail]);
  const strengthUnit = useCompanyStrengthUnit(gradeCompanyId);
  const [virtualLocations, setVirtualLocations] = useState<PourLocation2[]>([]);
  const [locationRoles, setLocationRoles] = useState<Record<string, CurveRole>>({});
  const [running, setRunning] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Pour whose realtime replay this page started (live readings running).
  const [livePourId, setLivePourId] = useState('');
  const [result, setResult] = useState<string>('');

  // Data start defaults to now minus the curing hours, so the data ends at now.
  const startTimeFor = (curingHours: number) => {
    const start = new Date(Date.now() - curingHours * 3600000);
    const tzOffset = start.getTimezoneOffset() * 60000;
    return new Date(start.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  const applyPourDefaults = async (id: string) => {
    setPourId(id);
    setSelectedPourDetail(null);
    setVirtualLocations([]);
    setLocationRoles({});
    if (!id) return;

    // Load the pour's virtual-logger locations for per-location curve roles.
    // Failure here is non-fatal — roles just default to Auto server-side.
    poursAPI
      .getPourLocations2(id)
      .then((locs) => {
        const virtual = locs.filter((l) => l.LoggerID === VIRTUAL_LOGGER_ID);
        setVirtualLocations(virtual);
        setLocationRoles(
          Object.fromEntries(
            virtual.map((l) => [String(l.ChannelNo), roleFromName(l.LocationDescription || l.LocationID)]),
          ),
        );
      })
      .catch((err) => console.error('Failed to load pour locations:', err));

    try {
      const detail = await poursAPI.getPourDetail(id);
      setSelectedPourDetail(detail);

      const funcs = detail.MonitoringFunctions || '';
      const hasWeatherFunc = funcs.includes('Onsite Weather') || funcs.includes('Evaporation');
      const hasMaturity = funcs.includes('Concrete Maturity');
      const hasTMC = funcs.includes('Temperature Match Curing');

      const curingHours = defaultCuringHours(detail, parseInt(hours) || 24);
      setHours(String(curingHours));

      // Start time: default to now minus curing hours so data ends at ~now
      setStartTime(startTimeFor(curingHours));

      // Weather: auto-enable if the pour uses weather/evaporation functions
      setIncludeWeather(hasWeatherFunc);

      // Peak temp: higher for mass concrete / TMC pours
      if (hasTMC) {
        setPeakTemp('70');
        setBaseTemp('20');
        setPeakPercent('25');
      } else if (hasMaturity) {
        setPeakTemp('60');
        setBaseTemp('20');
        setPeakPercent('30');
      } else {
        setPeakTemp('65');
        setBaseTemp('20');
        setPeakPercent('30');
      }
    } catch (err) {
      console.error('Failed to load pour detail for auto-populate:', err);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await loadLivePours();
        if (!cancelled) setPours(res);
      } catch (err: any) {
        if (!cancelled) setLoadError(apiErrorText(err, 'Failed to load pours'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Projects for the quick-create panel; loading lookups also seeds the
    // virtual logger server-side, so it exists before the first pour.
    poursAPI
      .getLookups()
      .then((lk) => {
        if (!cancelled) setProjects(lk.projects || []);
      })
      .catch((err) => console.error('Failed to load lookups:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  const inProgressPours = pours.filter((p) =>
    ['in progress', 'pending'].includes(p.PourStatus?.toLowerCase() || ''),
  );

  const handleQuickCreate = async () => {
    if (!qcProjectId) return;
    setQcRunning(true);
    setQcResult('');
    try {
      const res = await poursAPI.quickCreateSimPour({
        project_id: qcProjectId,
        kind: qcKind,
        num_locations: parseInt(qcLocations) || 3,
        hours: parseInt(qcHours) || 48,
        pour_name: qcName.trim() || undefined,
      });
      setQcResult(res.message);
      setQcName('');
      setPours(await loadLivePours());
      // Auto-select the new pour so it's ready to simulate straight away,
      // with the curing hours asked for (its stop time has a spare day).
      await applyPourDefaults(res.pour_id);
      const qcCuringHours = parseInt(qcHours) || 48;
      setHours(String(qcCuringHours));
      setStartTime(startTimeFor(qcCuringHours));
    } catch (err: any) {
      setQcResult('Error: ' + extractApiError(err));
    } finally {
      setQcRunning(false);
    }
  };

  const handleSimulate = async () => {
    if (!pourId) return;
    setRunning(true);
    setResult('');
    try {
      const res = await poursAPI.simulateData(pourId, {
        mode,
        hours: parseInt(hours) || 24,
        base_temp: parseFloat(baseTemp) || 20,
        peak_temp: parseFloat(peakTemp) || 65,
        peak_hour_percent: parseFloat(peakPercent) || 30,
        noise: parseFloat(noise) || 0.5,
        realtime_interval_seconds: parseInt(realtimeInterval) || 10,
        realtime_duration_minutes: parseInt(realtimeDuration) || 10,
        start_time: mode === 'bulk' && startTime ? new Date(startTime).toISOString() : undefined,
        include_weather: includeWeather,
        weather_temp: parseFloat(weatherTemp) || 25,
        weather_humidity: parseFloat(weatherHumidity) || 60,
        weather_wind: parseFloat(weatherWind) || 11,
        location_roles: Object.fromEntries(
          Object.entries(locationRoles).filter(([, role]) => role !== 'auto'),
        ),
      });
      // Any run replaces this pour's earlier replay; only realtime starts a new one.
      setLivePourId(res.live ? pourId : (livePourId === pourId ? '' : livePourId));
      setResult(res.message);
    } catch (err: any) {
      setResult('Error: ' + extractApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const handleStopLive = async () => {
    if (!pourId) return;
    setStopping(true);
    setResult('');
    try {
      const res = await poursAPI.stopSimulation(pourId);
      if (livePourId === pourId) setLivePourId('');
      setResult(res.message);
    } catch (err: any) {
      setResult('Error: ' + extractApiError(err));
    } finally {
      setStopping(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!pourId) return;
    setCompleting(true);
    setResult('');
    try {
      const res = await poursAPI.stopPour(pourId);
      setResult(
        res.already_stopped
          ? 'Pour was already complete.'
          : 'Pour marked complete: it will no longer appear in the In Progress list.',
      );
      if (livePourId === pourId) setLivePourId('');
      setPours(await loadLivePours());
      setPourId('');
      setSelectedPourDetail(null);
      setVirtualLocations([]);
      setLocationRoles({});
    } catch (err: any) {
      setResult('Error: ' + extractApiError(err));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {t('simulator.title', 'Simulate Logger Data')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t(
          'simulator.description',
          "Generate simulated temperature data using the virtual logger. Pour must have VIRTUAL-LOGGER assigned to its locations.",
        )}
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('simulator.quickCreateTitle', 'Create Test Pour')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t(
            'simulator.quickCreateHelp',
            'One-click pour wired to the virtual logger. Temp Only picks up your temperature limits (Core/Surface differential preconfigured); Concrete Maturity picks a mix design with a calibration and its maturity targets. The pour is activated and selected below, ready to simulate.',
          )}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('simulator.project', 'Project')}</InputLabel>
            <Select
              value={qcProjectId}
              label={t('simulator.project', 'Project')}
              onChange={(e: SelectChangeEvent) => setQcProjectId(e.target.value)}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={qcKind}
            exclusive
            onChange={(_, v) => v && setQcKind(v)}
            size="small"
            fullWidth
          >
            <ToggleButton value="temp_only">{t('simulator.kindTempOnly', 'Temp Only (Mass Concrete)')}</ToggleButton>
            <ToggleButton value="maturity">{t('simulator.kindMaturity', 'Concrete Maturity')}</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 1.5 }}>
            <TextField
              label={t('simulator.qcLocations', 'Locations')}
              type="number"
              size="small"
              value={qcLocations}
              onChange={(e) => setQcLocations(e.target.value)}
              inputProps={{ min: 1, max: 4 }}
              helperText={t('simulator.qcLocationsHelp', 'Core, Surface, Ambient, Mid')}
            />
            <TextField
              label={t('simulator.qcHours', 'Curing hours')}
              type="number"
              size="small"
              value={qcHours}
              onChange={(e) => setQcHours(e.target.value)}
              inputProps={{ min: 1, max: 2160 }}
            />
            <TextField
              label={t('simulator.qcName', 'Pour name (optional)')}
              size="small"
              value={qcName}
              onChange={(e) => setQcName(e.target.value)}
              inputProps={{ maxLength: 50 }}
            />
          </Box>

          {qcResult && (
            <Alert severity={qcResult.startsWith('Error') ? 'error' : 'success'}>
              {qcResult}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleQuickCreate}
              disabled={!qcProjectId || qcRunning}
            >
              {qcRunning ? <CircularProgress size={20} /> : t('simulator.quickCreate', 'Create Pour')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={1} sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('simulator.pour', 'Pour')}</InputLabel>
              <Select
                value={pourId}
                label={t('simulator.pour', 'Pour')}
                onChange={(e: SelectChangeEvent) => applyPourDefaults(e.target.value)}
              >
                {inProgressPours.map((p) => (
                  <MenuItem key={p.PourID} value={p.PourID}>
                    {[p.PourCode, p.PourName].filter(Boolean).join(' - ') +
                      (p.ProjectName ? ` (${p.ProjectName})` : '')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedPourDetail && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                  Pour Configuration Detected
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1 }}>
                  {selectedPourDetail.MonitoringFunctions && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Functions</Typography>
                      <Typography variant="body2">{selectedPourDetail.MonitoringFunctions}</Typography>
                    </Box>
                  )}
                  {selectedPourDetail.XHours != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Stop After</Typography>
                      <Typography variant="body2">
                        {selectedPourDetail.StopType === '31'
                          ? `${selectedPourDetail.XHours} days (${selectedPourDetail.XHours * 24}h)`
                          : `${selectedPourDetail.XHours} hours`}
                      </Typography>
                    </Box>
                  )}
                  {selectedPourDetail.DatumTemp != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Datum Temp</Typography>
                      <Typography variant="body2">{selectedPourDetail.DatumTemp}°C</Typography>
                    </Box>
                  )}
                  {selectedPourDetail.MaturityMethod && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Maturity Method</Typography>
                      <Typography variant="body2">{selectedPourDetail.MaturityMethod}</Typography>
                    </Box>
                  )}
                  {selectedPourDetail.ProductCode && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Product</Typography>
                      <Typography variant="body2">{selectedPourDetail.ProductCode}</Typography>
                    </Box>
                  )}
                  {selectedPourDetail.ProductStrengthGrade != null && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Strength Grade</Typography>
                      <Typography variant="body2">{valueWithUnit(selectedPourDetail.ProductStrengthGrade, strengthUnit)}</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => v && setMode(v)}
              size="small"
              fullWidth
            >
              <ToggleButton value="bulk">{t('simulator.modeBulk', 'Bulk (Historical)')}</ToggleButton>
              <ToggleButton value="realtime">{t('simulator.modeRealtime', 'Realtime (Live)')}</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {mode === 'bulk'
                ? t(
                    'simulator.modeBulkHelp',
                    'Writes the whole history at once; maturity, strength and targets are calculated by the real engine.',
                  )
                : t(
                    'simulator.modeRealtimeHelp',
                    'Writes a history up to now, then adds a live reading every {{seconds}} seconds for {{minutes}} minutes.',
                    { seconds: realtimeInterval, minutes: realtimeDuration },
                  )}
            </Typography>

            {mode === 'bulk' && (
              <TextField
                label="Data start time"
                type="datetime-local"
                size="small"
                fullWidth
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="Auto-set to now minus curing hours. Adjust if needed, or clear for default."
              />
            )}

            <Divider />

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {t('simulator.tempCurve', 'Temperature Curve')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              <TextField
                label={t('simulator.curingHours', 'Curing hours')}
                type="number"
                size="small"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                inputProps={{ min: 1, max: 168 }}
              />
              <TextField
                label={t('simulator.peakAt', 'Peak at (%)')}
                type="number"
                size="small"
                value={peakPercent}
                onChange={(e) => setPeakPercent(e.target.value)}
                inputProps={{ min: 5, max: 80 }}
                helperText={t('simulator.peakAtHelp', '% of duration')}
              />
              <TextField
                label={t('simulator.baseTemp', 'Base temp (°C)')}
                type="number"
                size="small"
                value={baseTemp}
                onChange={(e) => setBaseTemp(e.target.value)}
              />
              <TextField
                label={t('simulator.peakTemp', 'Peak temp (°C)')}
                type="number"
                size="small"
                value={peakTemp}
                onChange={(e) => setPeakTemp(e.target.value)}
              />
              <TextField
                label={t('simulator.noise', 'Noise (±°C)')}
                type="number"
                size="small"
                value={noise}
                onChange={(e) => setNoise(e.target.value)}
                inputProps={{ min: 0, max: 5, step: 0.1 }}
              />
            </Box>

            {virtualLocations.length > 0 && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('simulator.locationRoles', 'Location Curve Roles')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(
                    'simulator.locationRolesHelp',
                    'Assign deterministic curves per location to test temperature differentials: Core runs hottest and cools slowly, Surface peaks lower and cools fast, Ambient tracks air temperature. Auto keeps the legacy random ±3°C offset.',
                  )}
                </Typography>
                {virtualLocations.map((loc) => (
                  <Box key={loc.ID} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                      {loc.LocationDescription || loc.LocationID} · ch.{loc.ChannelNo}
                    </Typography>
                    <ToggleButtonGroup
                      value={locationRoles[String(loc.ChannelNo)] ?? 'auto'}
                      exclusive
                      size="small"
                      onChange={(_, v: CurveRole | null) =>
                        v && setLocationRoles((prev) => ({ ...prev, [String(loc.ChannelNo)]: v }))
                      }
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <ToggleButton key={r.value} value={r.value} title={r.help} sx={{ px: 1.25 }}>
                          {r.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                ))}
              </>
            )}

            {mode === 'realtime' && (
              <>
                <Divider />
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {t('simulator.realtimeSettings', 'Realtime Settings')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <TextField
                    label={t('simulator.pointEvery', 'Point every (sec)')}
                    type="number"
                    size="small"
                    value={realtimeInterval}
                    onChange={(e) => setRealtimeInterval(e.target.value)}
                    inputProps={{ min: 5, max: 300 }}
                    helperText={t('simulator.pointEveryHelp', 'Seconds between points')}
                  />
                  <TextField
                    label={t('simulator.realtimeDuration', 'Duration (min)')}
                    type="number"
                    size="small"
                    value={realtimeDuration}
                    onChange={(e) => setRealtimeDuration(e.target.value)}
                    inputProps={{ min: 1, max: 60 }}
                    helperText={t('simulator.realtimeDurationHelp', 'How long live readings run')}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {t(
                    'simulator.realtimeNote',
                    'The history covers the curing hours above. Live readings run in the API and stop if it restarts.',
                  )}
                </Typography>
              </>
            )}

            <Divider />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeWeather}
                  onChange={(e) => setIncludeWeather(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('simulator.includeWeather', 'Include weather data')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      'simulator.includeWeatherHelp',
                      'Adds air temp, humidity & wind speed to each reading. Required for the evaporation chart; only works if the pour\'s location has "Calculate Evaporation" enabled.',
                    )}
                  </Typography>
                </Box>
              }
            />
            {includeWeather && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
                <TextField
                  label={t('simulator.airTemp', 'Air temp (°C)')}
                  type="number"
                  size="small"
                  value={weatherTemp}
                  onChange={(e) => setWeatherTemp(e.target.value)}
                />
                <TextField
                  label={t('simulator.humidity', 'Humidity (%)')}
                  type="number"
                  size="small"
                  value={weatherHumidity}
                  onChange={(e) => setWeatherHumidity(e.target.value)}
                />
                <TextField
                  label={t('simulator.windKmh', 'Wind (km/h)')}
                  type="number"
                  size="small"
                  value={weatherWind}
                  onChange={(e) => setWeatherWind(e.target.value)}
                />
              </Box>
            )}

            {result && (
              <Alert severity={result.startsWith('Error') ? 'error' : 'success'} sx={{ mt: 1 }}>
                {result}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              {(mode === 'realtime' || (livePourId !== '' && livePourId === pourId)) && (
                <Button
                  variant="outlined"
                  onClick={handleStopLive}
                  disabled={!pourId || stopping || running}
                  title={t('simulator.stopLiveHelp', 'Stop adding live readings to this pour. Readings already written stay.')}
                >
                  {stopping ? <CircularProgress size={20} /> : t('simulator.stopLive', 'Stop Live Readings')}
                </Button>
              )}
              <Button
                variant="outlined"
                color="warning"
                onClick={handleMarkComplete}
                disabled={!pourId || completing || running}
                title={t('simulator.markCompleteHelp', 'Stop the pour (Status → Complete) so it drops off the In Progress list without deleting it.')}
              >
                {completing ? <CircularProgress size={20} /> : t('simulator.markComplete', 'Mark Complete')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSimulate}
                disabled={!pourId || running || completing}
              >
                {running ? <CircularProgress size={20} /> : t('simulator.generate', 'Generate Data')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SimulatorPage;
